import { dayjsWithExtensions } from '../../../../../utils/dayjs-config';
import type { Dayjs } from 'dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import SharedDialog from '../../../../../shared/shared-dialog';
import { useState } from 'react';
import { Calendar, CalendarEventModel } from '../../../../../../types';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { UserKeys, createCalendarEvent } from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import {
  formatEventStartPayload,
  formatEventEndPayload,
} from '../../../../../utils/calendar-events-utils';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import SharedAutocomplete from '../../../../../shared/shared-autocomplete';
import AvatarChip from '../../../../../shared/avatar-chip';
import { calendarColors } from '../../../../../utils/calendar-colors';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface CalendarAddEventDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  changeLogRoles: string[];
  isAllUsers: boolean;
  selectedCalendar: Calendar;
  selectedStartDateTime: number | null;
  selectedEndDateTime: number | null;
  dateRange: {
    start: number;
    end: number;
  };
}

export default function CalendarAddEventDialog({
  open,
  handleClose,
  userEmail,
  changeLogRoles,
  isAllUsers,
  selectedCalendar,
  selectedStartDateTime,
  selectedEndDateTime,
  dateRange,
}: CalendarAddEventDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isCreating } = useMutation({
    mutationFn: (data: FormValues) => {
      return createCalendarEvent(
        selectedCalendar.id,
        true,
        {
          summary: data.summary || '',
          location: data.location,
          description: data.description,
          colorId: data.colorId === 'default' ? null : data.colorId,
          creator: {
            email: userEmail,
          },
          start: formatEventStartPayload(data.start),
          end: formatEventEndPayload(data.start, data.end),
          attendees: data.invitedUsers.map((user) => {
            return {
              email: user.email,
            };
          }),
        },
        changeLogMessage
      );
    },
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Calendar event created successfully!',
      });

      queryClient.setQueriesData(
        {
          queryKey: [
            UserKeys.CalendarEvents,
            selectedCalendar.id,
            userEmail,
            {
              start: dateRange.start,
              end: dateRange.end,
            },
          ],
        },
        (oldData: CalendarEventModel[] | undefined) => {
          if (oldData) {
            return [...oldData, data];
          } else {
            return [data];
          }
        }
      );
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');
      reset();
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue creating a calendar event. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        isAllUsers
          ? analytics.calendars.calendars.create_calendar_event
          : analytics.user_management.user_details.calendars
              .create_calendar_event,
        isAllUsers
          ? {
              module: ModuleList.modules.calendars.name,
              section: ModuleList.modules.calendars.sections.calendars,
            }
          : {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.calendars,
            }
      );
      setOpenChangeLogDialog(false);
    },
  });

  interface FormValues {
    summary: string;
    start: {
      dateTime: Dayjs | null;
      allDay: boolean;
    };
    end: {
      dateTime: Dayjs | null;
      allDay: boolean;
    };
    location: string;
    description: string;
    invitedUsers: {
      email: string;
      type: string;
    }[];
    colorId: string;
  }

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    formState: { isLoading },
  } = useForm<FormValues>({
    defaultValues: {
      summary: '',
      start: {
        dateTime: selectedStartDateTime
          ? dayjsWithExtensions(selectedStartDateTime)
          : null,
        allDay: false,
      },
      end: {
        dateTime: selectedEndDateTime
          ? dayjsWithExtensions(selectedEndDateTime)
          : null,
        allDay: false,
      },
      location: '',
      description: '',
      invitedUsers: [],
      colorId: 'default',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'invitedUsers',
  });

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    // Validate that end date is not before start date
    if (data.start.dateTime && data.end.dateTime) {
      const startDate = dayjsWithExtensions(data.start.dateTime);
      const endDate = dayjsWithExtensions(data.end.dateTime);
      if (endDate.isBefore(startDate)) {
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'End date must be on or after the start date.',
        });
        return;
      }
    }

    if (isAbleToSeeUIElement(changeLogRoles, CalendarRoles.AddEvents.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const allDay = watch('start.allDay');
  const startDateTime = watch('start.dateTime');

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        reset();
      }}
      title="Create Event"
      dialogContent={
        <Stack gap={1.5} justifyContent="flex-start">
          <Stack flexDirection="row" gap={1}>
            <Controller
              name="summary"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoFocus
                  label="Title"
                  autoComplete="off"
                  sx={{ flexGrow: 1 }}
                />
              )}
            />
            <Controller
              name="colorId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'colorId',
                  }}
                  InputLabelProps={{
                    htmlFor: 'colorId',
                  }}
                  select
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                        },
                      },
                    },
                  }}
                  label="Color"
                  sx={{ width: 80 }}
                >
                  <MenuItem key={'default'} value={'default'}>
                    <Box
                      sx={{
                        backgroundColor:
                          calendarColors.calendar[
                            selectedCalendar.colorId as keyof typeof calendarColors.event
                          ].background,
                        width: 20,
                        height: 20,
                        mx: 'auto',
                      }}
                    ></Box>
                  </MenuItem>
                  {Object.keys(calendarColors.event).map((colorId) => (
                    <MenuItem key={colorId} value={colorId}>
                      <Box
                        sx={{
                          backgroundColor:
                            calendarColors.event[
                              colorId as keyof typeof calendarColors.event
                            ].background,
                          width: 20,
                          height: 20,
                          mx: 'auto',
                        }}
                      ></Box>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>

          <Box>
            <Stack flexDirection="row" justifyContent="space-between">
              <Controller
                name="start.dateTime"
                control={control}
                render={({ field: { value, onChange } }) => {
                  if (allDay) {
                    return (
                      <DatePicker
                        value={value}
                        onChange={(newValue) => {
                          if (newValue) {
                            onChange(newValue);
                            setValue('end.dateTime', newValue, {
                              shouldDirty: true,
                            });
                          }
                        }}
                        label="From"
                        sx={{ minWidth: 220 }}
                      />
                    );
                  } else {
                    return (
                      <DateTimePicker
                        value={value}
                        onChange={(newValue) => {
                          if (newValue) {
                            onChange(newValue);
                            setValue(
                              'end.dateTime',
                              newValue.add(30, 'minutes'),
                              {
                                shouldDirty: true,
                              }
                            );
                          }
                        }}
                        label="From"
                        sx={{ minWidth: 220 }}
                      />
                    );
                  }
                }}
              />
              <Controller
                name="end.dateTime"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value || !startDateTime) return true;
                    const endDate = dayjsWithExtensions(value);
                    const startDate = dayjsWithExtensions(startDateTime);
                    return (
                      !endDate.isBefore(startDate) ||
                      'End date must be on or after the start date'
                    );
                  },
                }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => {
                  if (allDay) {
                    return (
                      <DatePicker
                        value={value}
                        onChange={onChange}
                        label="To"
                        sx={{ minWidth: 220 }}
                        minDate={startDateTime ?? undefined}
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: error?.message || ' ',
                          },
                        }}
                      />
                    );
                  }
                  return (
                    <DateTimePicker
                      value={value}
                      onChange={onChange}
                      label="To"
                      sx={{ minWidth: 220 }}
                      minDateTime={startDateTime ?? undefined}
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message || ' ',
                        },
                      }}
                    />
                  );
                }}
              />
            </Stack>
            <Controller
              name="start.allDay"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                      name="calendarEvents"
                    />
                  }
                  label="All Day"
                />
              )}
            />
          </Box>
          <Stack flexDirection="row" gap={1}>
            <Box sx={{ flexGrow: 1 }}>
              <SharedAutocomplete
                sources={['users', 'groups']}
                autocompleteValue={autoCompleteValue}
                setAutoCompleteValue={setAutoCompleteValue}
                onSelection={(value, type) => {
                  if (!value) return;
                  append({
                    email: value,
                    type: type || 'user',
                  });
                  setAutoCompleteValue('');
                }}
              />
            </Box>
          </Stack>
          <Stack
            flexDirection="row"
            sx={{
              maxHeight: 80,
              overflowY: 'auto',
              flexWrap: 'wrap',
            }}
            gap={0.5}
          >
            {fields.map((field, index) => {
              return (
                <AvatarChip
                  key={field.email}
                  email={field.email}
                  type={field.type as 'Group' | 'User' | 'Domain' | 'OrgUnit'}
                  onDelete={() => remove(index)}
                />
              );
            })}
          </Stack>

          <Controller
            name="location"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Location"
                autoComplete="off"
                sx={{ minWidth: 250 }}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                autoComplete="off"
                sx={{ minWidth: 250 }}
                multiline
                rows={3}
              />
            )}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              return mutate(getValues());
            }}
            mutateLoading={isCreating}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Create Event',
        action: handleSubmit(onSubmit),
        loading: isCreating || isLoading,
        disabled: isCreating || isLoading,
      }}
    />
  );
}
